import type { QuickRebuttal } from "@expert/contentful";

import { Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import clsx from "clsx";

import styles from "./NarrativeRebuttalCard.module.css";

export interface NarrativeRebuttalCardProps {
    /** The product's Contentful quick-rebuttal doc (objections → responses). */
    doc: QuickRebuttal;
    /** Dismiss the sheet entirely; the footer "Quick rebuttal" button re-opens it. */
    onDismiss: () => void;
    /** Start expanded (≈75% height). Default true — opening the sheet expands it. */
    defaultExpanded?: boolean;
}

const quote = (s = "") => `“${s}”`;
// The "Early Rebuttal" is a section label, not an objection quote, so it renders unquoted —
// mirroring formatQuickRebuttal's quoting so this inline sheet and the legacy checklist footer (which
// still uses formatQuickRebuttal) read identically.
const renderTitle = (title?: string) => (title === "Early Rebuttal" ? title : quote(title));

/**
 * The quick-rebuttal reference, shown INLINE in the Long Form AI surface (Offers tab) rather than
 * handing off to the Expert Assist tab (design: Ricardo 2026-08-27 — keep the expert in the flow).
 * Renders as a bottom sheet that slides up from the base of the surface: expanded it takes ≈75% of
 * the container height with a scrollable body; contracted it collapses to just its header bar. The
 * expert expands/contracts it with the chevron and closes it with ✕.
 */
export const NarrativeRebuttalCard = ({ doc, onDismiss, defaultExpanded = true }: NarrativeRebuttalCardProps) => {
    const [expanded, { toggle }] = useDisclosure(defaultExpanded);
    const objections = doc.objections ?? [];
    if (objections.length === 0) return null;

    return (
        <section className={clsx(styles.sheet, expanded && styles.sheetExpanded)} aria-label="Objection handling">
            <Group className={styles.header} justify="space-between" wrap="nowrap" gap={8}>
                <UnstyledButton
                    className={styles.toggle}
                    onClick={toggle}
                    aria-expanded={expanded}
                    aria-label={expanded ? "Contract rebuttals" : "Expand rebuttals"}
                >
                    <span className={styles.eyebrow}>Objection handling</span>
                    <IconChevronDown
                        size={16}
                        className={clsx(styles.chevron, !expanded && styles.chevronUp)}
                        aria-hidden
                    />
                </UnstyledButton>
                <UnstyledButton className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss rebuttals">
                    <IconX size={16} />
                </UnstyledButton>
            </Group>

            {expanded && (
                <div className={styles.body}>
                    <Stack gap={14}>
                        {objections.map((objection, i) => (
                            <div key={objection.id ?? i}>
                                <Text component="p" my={0} className={styles.objectionTitle}>
                                    {i + 1}. {renderTitle(objection.title)}
                                </Text>
                                {(objection.responses ?? []).length > 0 && (
                                    <ul className={styles.responses}>
                                        {(objection.responses ?? []).map((response, j) => (
                                            <li key={response.id ?? j} className={styles.responseItem}>
                                                {quote(response.text)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        ))}
                    </Stack>
                </div>
            )}
        </section>
    );
};
